import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  listAuditExceptions,
  listAuditPrompts,
  listAuditRuns,
} from "@/lib/api";
import { fmtDateTime, relativeAge } from "@/lib/format";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/Table";
import { Tabs } from "@/components/ui/Tabs";
import {
  ExceptionSeverityBadge,
  RunStatusBadge,
} from "@/components/ui/Badges";
import { LoadingState, EmptyState } from "@/components/ui/States";

type AuditTab = "runs" | "prompts" | "exceptions";

export function AuditPage() {
  const [tab, setTab] = useState<AuditTab>("runs");

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Audit</h1>
          <p className="text-2xs text-foreground-muted">
            Append-only model runs, LLM prompts, and pipeline exceptions.
          </p>
        </div>
        <Tabs<AuditTab>
          value={tab}
          onChange={setTab}
          options={[
            { value: "runs", label: "Model runs" },
            { value: "prompts", label: "Prompts" },
            { value: "exceptions", label: "Exceptions" },
          ]}
        />
      </header>

      {tab === "runs" && <RunsTable />}
      {tab === "prompts" && <PromptsTable />}
      {tab === "exceptions" && <ExceptionsTable />}
    </div>
  );
}

function RunsTable() {
  const runs = useQuery({
    queryKey: ["audit", "runs"],
    queryFn: () => listAuditRuns({ limit: 100 }),
  });
  return (
    <Panel bodyClassName="px-0 py-0">
      {runs.isLoading && <LoadingState />}
      {runs.data && runs.data.length === 0 && (
        <EmptyState
          title="No model runs yet"
          description="Each scoring, policy, or memo execution writes a run row here."
        />
      )}
      {runs.data && runs.data.length > 0 && (
        <DataTable
          dense
          rows={runs.data}
          rowKey={(r) => r.id}
          columns={[
            { key: "id", header: "Id", cell: (r) => `#${r.id}`, align: "right" },
            { key: "kind", header: "Kind", cell: (r) => r.kind },
            {
              key: "status",
              header: "Status",
              cell: (r) => <RunStatusBadge status={r.status} />,
            },
            {
              key: "wv",
              header: "Weight vec",
              cell: (r) => r.weight_vector_id ?? "—",
              align: "right",
            },
            {
              key: "pv",
              header: "Policy vsn",
              cell: (r) => r.policy_version_id ?? "—",
              align: "right",
            },
            {
              key: "started",
              header: "Started",
              cell: (r) => relativeAge(r.started_at),
            },
            {
              key: "duration",
              header: "Duration",
              cell: (r) => durationText(r.started_at, r.finished_at ?? null),
              align: "right",
            },
            {
              key: "hash",
              header: "Inputs hash",
              cell: (r) => (
                <code className="text-2xs text-foreground-muted">{r.inputs_hash ?? "—"}</code>
              ),
            },
            {
              key: "error",
              header: "Error",
              cell: (r) =>
                r.error_text ? (
                  <span className="text-2xs text-danger" title={r.error_text}>
                    {r.error_text.slice(0, 60)}
                  </span>
                ) : (
                  "—"
                ),
            },
          ]}
        />
      )}
    </Panel>
  );
}

function PromptsTable() {
  const prompts = useQuery({
    queryKey: ["audit", "prompts"],
    queryFn: () => listAuditPrompts({ limit: 100 }),
  });
  return (
    <Panel bodyClassName="px-0 py-0">
      {prompts.isLoading && <LoadingState />}
      {prompts.data && prompts.data.length === 0 && (
        <EmptyState
          title="No prompt log entries"
          description="LLM calls land here. Generate a memo to populate."
        />
      )}
      {prompts.data && prompts.data.length > 0 && (
        <DataTable
          dense
          rows={prompts.data}
          rowKey={(r) => r.id}
          columns={[
            { key: "id", header: "Id", cell: (r) => `#${r.id}`, align: "right" },
            {
              key: "template",
              header: "Template",
              cell: (r) => (
                <code className="text-2xs text-foreground-secondary">{r.template_id}</code>
              ),
            },
            { key: "model", header: "Model", cell: (r) => r.model },
            {
              key: "in",
              header: "Tok in",
              cell: (r) => r.tokens_in ?? "—",
              align: "right",
            },
            {
              key: "out",
              header: "Tok out",
              cell: (r) => r.tokens_out ?? "—",
              align: "right",
            },
            {
              key: "cost",
              header: "¢",
              cell: (r) => r.cost_cents ?? "—",
              align: "right",
            },
            {
              key: "ts",
              header: "When",
              cell: (r) => fmtDateTime(r.ts),
            },
            {
              key: "error",
              header: "Error",
              cell: (r) =>
                r.error_text ? (
                  <span className="text-2xs text-danger" title={r.error_text}>
                    {r.error_text.slice(0, 50)}
                  </span>
                ) : (
                  "—"
                ),
            },
          ]}
        />
      )}
    </Panel>
  );
}

function ExceptionsTable() {
  const exceptions = useQuery({
    queryKey: ["audit", "exceptions"],
    queryFn: () => listAuditExceptions({ limit: 100 }),
  });
  return (
    <Panel bodyClassName="px-0 py-0">
      {exceptions.isLoading && <LoadingState />}
      {exceptions.data && exceptions.data.length === 0 && (
        <EmptyState title="No exceptions" description="Pipelines are healthy." />
      )}
      {exceptions.data && exceptions.data.length > 0 && (
        <DataTable
          dense
          rows={exceptions.data}
          rowKey={(r) => r.id}
          columns={[
            { key: "id", header: "Id", cell: (r) => `#${r.id}`, align: "right" },
            {
              key: "service",
              header: "Service",
              cell: (r) => (
                <code className="text-2xs text-foreground-secondary">{r.service}</code>
              ),
            },
            {
              key: "severity",
              header: "Severity",
              cell: (r) => <ExceptionSeverityBadge severity={r.severity} />,
            },
            { key: "message", header: "Message", cell: (r) => r.message },
            {
              key: "ts",
              header: "When",
              cell: (r) => fmtDateTime(r.ts),
            },
          ]}
        />
      )}
    </Panel>
  );
}

function durationText(start: string, finish: string | null): string {
  if (!finish) return "—";
  const d = (new Date(finish).getTime() - new Date(start).getTime()) / 1000;
  if (!Number.isFinite(d) || d < 0) return "—";
  if (d < 1) return `${(d * 1000).toFixed(0)}ms`;
  if (d < 60) return `${d.toFixed(1)}s`;
  return `${(d / 60).toFixed(1)}m`;
}
